/**
 * Domains API client — customer `/my/domains` on the Domain object
 * (Unified Certificate Service design v1.5.1 §15, Phase 6.5).
 *
 * Mirrors the unified-envelope conventions used in `backup-api.js`,
 * `cluster-api.js`, and `billing-api.js`:
 *   - one axios instance off `envConfig`
 *   - best-effort Bearer attach via the request interceptor (the page route
 *     itself is auth-gated; this is defense in depth)
 *   - `unwrap(response)` resolves `{ success, message, data, ... }` into the
 *     bare `data` payload and throws on `success: false`.
 *
 * The Domain verbs (list / claim / get / patch / release / verify /
 * delegation / private lane / expected records) live in
 * `utils/domains-core.js` (pure, `node --test`-covered) and are bound here.
 * They target the canonical `/dns-manager/domains` routes and fall back to
 * the historic `/dns-manager/customer/domains` aliases when the canonical
 * route is missing (an older dns-manager).
 *
 * Typed errors (re-exported from the core) let the UI render specific copy
 * without sniffing status codes in components:
 *   - `DomainNotVerified`      — 403 from a record endpoint (TXT pending)
 *   - `DomainConflict`         — 409 from the claim endpoint (another org)
 *   - `PublicLaneSelfManaged`  — 409 `public_lane_self_managed`: a public
 *                                write on a provider-`none` domain (P-24);
 *                                carries `expectedRecords`
 *   - `DomainPolicyError`      — 422 `SUBDOMAIN_POLICY_REQUIRES_PUBLIC_PROVIDER`
 *                                and kin (A-22)
 *   - `DomainAccessDenied`     — 403 `forbidden` on the list: no `domain:viewer`
 *                                for this caller in this org (a Member without
 *                                a grant). `canViewDomains()` asks first, so
 *                                the 403 is normally never requested.
 *   - `DomainLimitReached`     — 409 `domain_limit_reached` from the claim: a
 *                                shared-tier caller already holds the one
 *                                personal domain their plan includes; its
 *                                message is the plain plan sentence.
 */
import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'
import { DomainLimitReached } from '@/utils/personal-domain'
import {
  createDomainsClient, createTtlCache, datacenterOptions, typedDomainError, unwrapEnvelope,
  probeDomainAccess,
  DomainNotVerified, DomainConflict, PublicLaneSelfManaged, DomainPolicyError, DomainAccessDenied
} from '@/utils/domains-core'

export { DomainNotVerified, DomainConflict, PublicLaneSelfManaged, DomainPolicyError, DomainAccessDenied, DomainLimitReached }

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

// dns-manager-api is reached at /api/dns-manager/* and cluster-api at
// /api/cluster/* through the same tunnel.
const ROOT = API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`

const dnsAxios = axios.create({
  baseURL: ROOT,
  // Verify / expected-records can take a few seconds while public resolvers
  // are queried; record-CRUD is usually <1s.
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT || 60000,
  headers: {
    'Content-Type': 'application/json'
  }
})

dnsAxios.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const unwrap = unwrapEnvelope

/** Rethrow a record-route error as its typed twin (403 → DomainNotVerified, 409 → PublicLaneSelfManaged). */
function rethrowRecord(error, domain) {
  throw typedDomainError(error, { domain, context: 'record' })
}

const domainsClient = createDomainsClient(dnsAxios, {
  unwrap,
  onFallback: (name) => {
    if (envConfig.VUE_APP_DEBUG) console.info(`[domains-api] canonical /domains route missing — used the /customer/domains alias for "${name}"`)
  }
})

/** `GET /api/cluster/datacenters` (read-only, cached 5 min). 403 for a customer → `[]`. */
const datacentersCache = createTtlCache(async() => {
  const response = await dnsAxios.get('/cluster/datacenters', { params: { include_assets: 'false' } })
  return datacenterOptions(unwrap(response) || [])
})

// ── Public surface ──────────────────────────────────────────────────────────

export const domainsApi = {
  // ── Domain object (canonical, alias fallback) ────────────────────────────
  /** GET /api/dns-manager/domains → Domain[] (org-scoped). */
  async listMyDomains() {
    return domainsClient.list()
  },

  /** GET /api/dns-manager/domains → `{domains, meta}`; `meta` = `{scope, limit, used}` or null (older dns-manager). */
  async listMyDomainsWithMeta() {
    return domainsClient.listWithMeta()
  },

  /**
   * GET /api/dns-manager/domains/access → `{canManage, scope, limit, used, reason}`,
   * or null when this dns-manager does not answer it (never throws).
   */
  async getDomainAccess() {
    return domainsClient.access()
  },

  /**
   * POST /api/auth/permissions/check-batch `{domain, viewer}` — the same
   * check dns-manager makes on the list. true / false, or null when the probe
   * failed (never throws).
   */
  async canViewDomains() {
    return probeDomainAccess(dnsAxios)
  },

  /**
   * POST /api/dns-manager/domains {domain, datacenter?, public_provider?, subdomain_policy?}
   * → flat row: `{ domain, verification_status, verification_token,
   *   verification_record: {type, name, value}, zone, datacenter,
   *   public_provider, private_lane, delegation, edge_state, … }`.
   * 201 first time, 200 idempotent re-claim, 409 → DomainConflict,
   * 422 → DomainPolicyError.
   */
  async claimDomain(payload) {
    const body = typeof payload === 'string' ? { domain: payload } : (payload || {})
    return domainsClient.claim(body)
  },

  /** GET /api/dns-manager/domains/{domain} → Domain. */
  async getDomain(domain) {
    return domainsClient.get(domain)
  },

  /** PATCH /api/dns-manager/domains/{domain} — datacenter / public_provider / provider_connection_id / subdomain_policy. */
  async patchDomain(domain, fields) {
    return domainsClient.patch(domain, fields)
  },

  /**
   * POST /api/dns-manager/domains/{domain}/verify → the verified row (200) or
   * the still-pending row (202 / success:false DOMAIN_NOT_VERIFIED) with
   * `verification_status: 'pending'`. Callers read the field, not the status.
   */
  async verifyDomain(domain) {
    return domainsClient.verify(domain)
  },

  /** DELETE /api/dns-manager/domains/{domain} — releases the claim. */
  async releaseDomain(domain) {
    return domainsClient.release(domain)
  },

  /** POST /api/dns-manager/domains/{domain}/delegation/check → `{domain, zone, delegation}`. */
  async checkDelegation(domain) {
    return domainsClient.checkDelegation(domain)
  },

  /** POST /api/dns-manager/domains/{domain}/private-lane/ensure → `{domain, zone, datacenter, private_lane}` (A-24). */
  async ensurePrivateLane(domain) {
    return domainsClient.ensurePrivateLane(domain)
  },

  /** GET /api/dns-manager/domains/{domain}/expected-records → `{records[], edge_ip, hostnames, …}` (P-24). */
  async expectedRecords(domain, opts) {
    return domainsClient.expectedRecords(domain, opts)
  },

  // ── Datacenters (cluster-api, read-only, cached) ─────────────────────────
  /** `GET /api/cluster/datacenters` → `[{name, label, location}]`, cached 5 min. */
  async listDatacenters({ force = false } = {}) {
    return datacentersCache.get({ force })
  },

  // ── Records (org-scoped by middleware; provider resolves from the Domain) ─
  /**
   * GET /api/dns-manager/domains/{domain}/records → `{ records, publicLane? }`.
   * For a provider-`none` domain the public side is skipped and
   * `publicLane: {provider: 'none', self_managed: true, expected_records_url}`
   * is returned alongside.
   */
  async listRecordsDetailed(domain) {
    try {
      const response = await dnsAxios.get(
        `/dns-manager/domains/${encodeURIComponent(domain)}/records`
      )
      const body = response?.data
      let data = body
      if (body && typeof body === 'object' && 'success' in body) {
        if (body.success === false) {
          const err = new Error(body.message || body.error || 'Failed to load records')
          err.response = response
          throw err
        }
        data = body.data
      }
      if (Array.isArray(data)) return { records: data, publicLane: null }
      return {
        records: data && Array.isArray(data.records) ? data.records : [],
        publicLane: (data && (data.publicLane || data.public_lane)) || null
      }
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /** GET /api/dns-manager/domains/{domain}/records → records[] (kept for callers that only need the list). */
  async listRecords(domain) {
    const { records } = await this.listRecordsDetailed(domain)
    return records
  },

  /**
   * POST /api/dns-manager/domains/{domain}/records[?provider=]
   * Body: { type, name, value, ttl }. `provider` is an optional override — the
   * default resolves from the Domain (public lane) on the server.
   */
  async createRecord(domain, { provider, type, name, value, ttl }) {
    try {
      const response = await dnsAxios.post(
        `/dns-manager/domains/${encodeURIComponent(domain)}/records`,
        { type, name, value, ttl },
        { params: provider ? { provider } : {} }
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /**
   * PUT /api/dns-manager/domains/{domain}/records/{recordId}
   * Body: { type, name, value, ttl }
   */
  async updateRecord(domain, recordId, payload) {
    try {
      const response = await dnsAxios.put(
        `/dns-manager/domains/${encodeURIComponent(domain)}/records/${encodeURIComponent(recordId)}`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /** DELETE /api/dns-manager/domains/{domain}/records/{recordId}[?provider=] */
  async deleteRecord(domain, recordId, provider) {
    try {
      const response = await dnsAxios.delete(
        `/dns-manager/domains/${encodeURIComponent(domain)}/records/${encodeURIComponent(recordId)}`,
        { params: provider ? { provider } : {} }
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  // ── Dual records (an A on both lanes) ────────────────────────────────────
  /** POST /api/dns-manager/domains/{domain}/dual-record  {name, publicIp, privateIp, ttl} */
  async createDualRecord(domain, payload) {
    try {
      const response = await dnsAxios.post(
        `/dns-manager/domains/${encodeURIComponent(domain)}/dual-record`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /** PUT /api/dns-manager/domains/{domain}/dual-record/{name} */
  async updateDualRecord(domain, name, payload) {
    try {
      const response = await dnsAxios.put(
        `/dns-manager/domains/${encodeURIComponent(domain)}/dual-record/${encodeURIComponent(name)}`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /** DELETE /api/dns-manager/domains/{domain}/dual-record/{name} */
  async deleteDualRecord(domain, name) {
    try {
      const response = await dnsAxios.delete(
        `/dns-manager/domains/${encodeURIComponent(domain)}/dual-record/${encodeURIComponent(name)}`
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  // ── Private lane (the platform's internal DNS) ───────────────────────────
  /** POST /api/dns-manager/domains/{domain}/private-record  {name, localIp, ttl, notes} */
  async createPrivateRecord(domain, { name, localIp, ttl, notes }) {
    try {
      const response = await dnsAxios.post(
        `/dns-manager/domains/${encodeURIComponent(domain)}/private-record`,
        { name, localIp, ttl, notes }
      )
      return unwrap(response)
    } catch (error) {
      rethrowRecord(error, domain)
    }
  },

  /** GET /api/dns-manager/domains/{domain}/private-records */
  async listPrivateRecords(domain) {
    try {
      const response = await dnsAxios.get(
        `/dns-manager/domains/${encodeURIComponent(domain)}/private-records`
      )
      const body = response?.data
      if (body && typeof body === 'object' && 'success' in body) {
        if (body.success === false) {
          const err = new Error(body.message || 'Failed to load private records')
          err.response = response
          throw err
        }
        const data = body.data
        if (Array.isArray(data)) return data
        if (data && Array.isArray(data.records)) return data.records
        return []
      }
      return Array.isArray(body) ? body : []
    } catch (error) {
      rethrowRecord(error, domain)
    }
  }
}

export default dnsAxios

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(dnsAxios)
